import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const FeatureGatingContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * FeatureGatingProvider - Centralized feature access management
 * 
 * Uses the enterprise subscription manager API endpoint:
 * GET /api/subscriptions/features
 * 
 * This is the SINGLE SOURCE OF TRUTH for feature gating in the frontend.
 * All components should use useFeatureGating() hook to check feature access.
 */
export function FeatureGatingProvider({ children }) {
  const [featureAccess, setFeatureAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  // Track auth state via user info (not JWT token - that's in httpOnly cookies)
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('user'));
  const fetchInProgressRef = useRef(false);

  // Listen for storage changes (login/logout in other tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        setIsAuthenticated(!!e.newValue);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Check auth on mount and after delay for race conditions
  useEffect(() => {
    let mounted = true;
    
    const hasUser = !!localStorage.getItem('user');
    if (mounted && hasUser !== isAuthenticated) {
      setIsAuthenticated(hasUser);
    }

    const timeoutId = setTimeout(() => {
      if (!mounted) return;
      const delayedUser = !!localStorage.getItem('user');
      if (delayedUser !== isAuthenticated) {
        setIsAuthenticated(delayedUser);
      }
    }, 100);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, [isAuthenticated]);

  const fetchFeatureAccess = useCallback(async () => {
    if (fetchInProgressRef.current) return;
    
    if (!isAuthenticated) {
      setFeatureAccess(getDefaultDemoFeatures());
      setLoading(false);
      return;
    }
    
    fetchInProgressRef.current = true;
    
    try {
      // Use axios (NOT fetch) so the global interceptor handles:
      // 1. Sending httpOnly cookies (withCredentials)
      // 2. Adding Bearer token from tokenStore (Safari fallback)
      // 3. Auto-refreshing expired tokens (401 → refresh → retry)
      const response = await axios.get(`${API}/api/subscriptions/features`);
      const data = response.data;
      console.log('FeatureGatingContext: Centralized API response', data);
      
      // Transform API response to context format
      const transformedData = transformApiResponse(data);
      setFeatureAccess(transformedData);
    } catch (error) {
      if (error.response?.status === 401) {
        console.error('FeatureGatingContext: Unauthorized');
        setIsAuthenticated(false);
        setFeatureAccess(getDefaultDemoFeatures());
      } else {
        // Fallback to legacy endpoint if new one fails
        console.warn('FeatureGatingContext: Falling back to legacy endpoint');
        try {
          const legacyResponse = await axios.get(`${API}/api/features/all`);
          setFeatureAccess(legacyResponse.data);
        } catch (legacyError) {
          setFeatureAccess(getDefaultDemoFeatures());
        }
      }
    } finally {
      setLoading(false);
      fetchInProgressRef.current = false;
    }
  }, [isAuthenticated]);

  // Fetch when auth state changes
  useEffect(() => {
    fetchFeatureAccess();
  }, [fetchFeatureAccess]);

  // Check if user is application owner (unlimited access)
  const isApplicationOwner = featureAccess?.is_application_owner ?? false;

  // Check if user is on active trial (full access, no restrictions)
  const isActiveTrial = featureAccess?.is_trial && !featureAccess?.trial_expired && featureAccess?.is_active;

  // Check if a specific feature is enabled
  const isFeatureEnabled = useCallback((feature) => {
    if (featureAccess?.is_application_owner) return true;
    // Active trial users get ALL features unlocked - zero restrictions
    if (featureAccess?.is_trial && !featureAccess?.trial_expired && featureAccess?.is_active) return true;
    if (featureAccess?.trial_full_access) return true;
    if (!featureAccess) return false;
    return featureAccess.features?.[feature] ?? false;
  }, [featureAccess]);

  // Get feature limit value
  const getLimit = useCallback((limitName) => {
    if (!featureAccess?.limits) return 0;
    const limit = featureAccess.limits[limitName];
    return limit === -1 ? Infinity : (limit ?? 0);
  }, [featureAccess]);

  // Check if a limit is exceeded
  const isLimitExceeded = useCallback((limitName, currentUsage) => {
    const limit = getLimit(limitName);
    if (limit === Infinity) return false;
    return currentUsage >= limit;
  }, [getLimit]);

  // Computed properties
  const isDemo = featureAccess?.is_demo ?? true;
  const isTrial = featureAccess?.is_trial ?? false;
  const isTrialExpired = featureAccess?.trial_expired ?? false;
  const showWatermark = featureAccess?.requires_watermark ?? true;
  const currentTier = featureAccess?.tier ?? 'demo';
  const effectiveTier = featureAccess?.effective_tier ?? featureAccess?.tier ?? 'demo';
  const daysRemaining = featureAccess?.days_remaining;
  const restrictedFeatures = featureAccess?.restricted_features ?? [];
  const validUntil = featureAccess?.valid_until;
  const pendingDowngrade = featureAccess?.pending_downgrade;
  const supportLevel = featureAccess?.support_level ?? 'community';

  // Refresh feature access (call after payment/upgrade or login)
  const refreshFeatureAccess = useCallback(() => {
    const hasUser = !!localStorage.getItem('user');
    if (hasUser !== isAuthenticated) {
      setIsAuthenticated(hasUser);
    } else {
      fetchInProgressRef.current = false;
      fetchFeatureAccess();
    }
  }, [isAuthenticated, fetchFeatureAccess]);

  // Quick check for a specific feature via API (for critical checks)
  const checkFeature = useCallback(async (featureName) => {
    if (!isAuthenticated) return { enabled: false, tier: 'demo', upgrade_required: true };
    
    try {
      const response = await axios.get(`${API}/api/subscriptions/features/check/${featureName}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to check feature ${featureName}:`, error);
    }
    return { enabled: isFeatureEnabled(featureName), tier: currentTier, upgrade_required: !isFeatureEnabled(featureName) };
  }, [isAuthenticated, isFeatureEnabled, currentTier]);

  const value = {
    // Core feature data
    featureAccess,
    loading,
    
    // Feature checks
    isFeatureEnabled,
    getLimit,
    isLimitExceeded,
    checkFeature,
    
    // User status
    isApplicationOwner,
    isActiveTrial,
    isDemo,
    isTrial,
    isTrialExpired,
    showWatermark,
    
    // Subscription info
    currentTier,
    effectiveTier,
    daysRemaining,
    validUntil,
    pendingDowngrade,
    supportLevel,
    
    // Features list
    restrictedFeatures,
    
    // Actions
    refreshFeatureAccess
  };

  return (
    <FeatureGatingContext.Provider value={value}>
      {children}
    </FeatureGatingContext.Provider>
  );
}

/**
 * Transform the centralized API response to the context format
 */
function transformApiResponse(data) {
  const tier = data.tier || 'demo';
  const isDemo = tier === 'demo';
  const isTrial = tier === 'trial';
  
  // Check application owner status from API response (primary) or localStorage (fallback)
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isApplicationOwner = data.is_application_owner === true ||
                              user.role === 'application_owner' || 
                              user.saas_role === 'application_owner' ||
                              user.saas_role === 'platform_admin';

  // Build restricted features list
  const restrictedFeatures = [];
  const featureNames = {
    invoices: 'Invoices',
    ota_integration: 'OTA Integration',
    bulk_booking: 'Bulk Booking',
    police_reports: 'Police Reports',
    shift_balance: 'Shift Balance',
    expenses: 'Expenses',
    analytics: 'Analytics',
    team_management: 'Team Management',
    integrations_management: 'Integrations',
    data_export: 'Data Export',
    api_access: 'API Access',
    custom_branding: 'Custom Branding',
    audit_logs: 'Audit Logs',
    multi_hotel: 'Multi-Hotel Management'
  };
  
  Object.entries(data.features || {}).forEach(([key, enabled]) => {
    if (!enabled && featureNames[key]) {
      restrictedFeatures.push(featureNames[key]);
    }
  });

  return {
    // Core subscription info
    tier: tier,
    tier_name: data.tier_name || tier.charAt(0).toUpperCase() + tier.slice(1),
    effective_tier: tier,
    is_active: data.is_active ?? false,
    
    // Status flags
    is_demo: isDemo,
    is_trial: isTrial,
    trial_expired: false,
    trial_full_access: data.trial_full_access ?? (isTrial && (data.is_active ?? false)),
    is_application_owner: isApplicationOwner,
    requires_watermark: data.requires_watermark ?? isDemo,
    watermark_enabled: data.requires_watermark ?? isDemo,
    
    // Features (from centralized resolver)
    features: data.features || {},
    
    // Limits (from centralized resolver)
    limits: data.limits || {},
    
    // Subscription validity
    valid_until: data.valid_until,
    days_remaining: calculateDaysRemaining(data.valid_until),
    
    // Pending downgrade info
    pending_downgrade: data.pending_downgrade,
    
    // Support level
    support_level: data.support_level || 'community',
    
    // Restricted features for UI
    restricted_features: restrictedFeatures
  };
}

/**
 * Calculate days remaining from valid_until date
 */
function calculateDaysRemaining(validUntil) {
  if (!validUntil) return null;
  try {
    const expiry = new Date(validUntil);
    const now = new Date();
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  } catch {
    return null;
  }
}

/**
 * Default demo features when no auth token
 */
function getDefaultDemoFeatures() {
  return {
    tier: 'demo',
    tier_name: 'Demo',
    effective_tier: 'demo',
    is_demo: true,
    is_trial: false,
    is_expired: false,
    trial_expired: false,
    is_application_owner: false,
    requires_watermark: true,
    watermark_enabled: true,
    is_active: false,
    features: {
      invoices: false,
      ota_integration: false,
      reports: true,
      housekeeping: true,
      bulk_booking: false,
      police_reports: false,
      shift_balance: false,
      expenses: false,
      analytics: false,
      team_management: false,
      integrations_management: false,
      data_export: false,
      api_access: false,
      custom_branding: false,
      audit_logs: false,
      multi_hotel: false
    },
    limits: {
      ocr_scans_per_month: 10,
      max_users: 1,
      max_rooms: 5,
      max_reservations_per_month: 20,
      data_retention_days: 7
    },
    valid_until: null,
    days_remaining: null,
    pending_downgrade: null,
    support_level: 'community',
    restricted_features: [
      'Invoices', 'OTA Integration', 'Bulk Booking', 'Police Reports', 
      'Shift Balance', 'Expenses', 'Analytics', 'Team Management', 
      'Integrations', 'Data Export', 'API Access', 'Custom Branding', 'Audit Logs',
      'Multi-Hotel Management'
    ]
  };
}

export function useFeatureGating() {
  const context = useContext(FeatureGatingContext);
  if (!context) {
    throw new Error('useFeatureGating must be used within a FeatureGatingProvider');
  }
  return context;
}

export default FeatureGatingContext;
